/** A review edit is saved independently of its later accept/reject decision. */
export class LiveReviewDraft<T extends object> {
  private remote: T;
  private pending: Partial<T> = {};
  private running = false;
  private active: Partial<T> = {};
  private listeners = new Set<() => void>();
  snapshot: { value: T; saving: boolean; error: string | null };
  constructor(value: T, public save: (patch: Partial<T>, value: T) => Promise<void>) {
    this.remote = value;
    this.snapshot = { value, saving: false, error: null };
  }
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  read = () => this.snapshot;
  private emit() { this.listeners.forEach(listener => listener()); }
  reconcile(remote: T) {
    const value = { ...this.snapshot.value };
    // Adopt remote changes to pristine fields; never erase local typing.
    for (const key of Object.keys(remote) as (keyof T)[]) {
      const previous = JSON.stringify(this.remote[key]);
      const pristine = JSON.stringify(value[key]) === previous;
      const changedRemotely = JSON.stringify(remote[key]) !== previous;
      const savingLocally = key in this.pending || key in this.active;
      if (!savingLocally && (pristine || (changedRemotely && !this.snapshot.error))) value[key] = remote[key];
    }
    this.remote = remote;
    if (JSON.stringify(value) !== JSON.stringify(this.snapshot.value)) {
      this.snapshot = { ...this.snapshot, value };
      this.emit();
    }
  }
  change = (next: T | ((previous: T) => T)) => {
    const value = typeof next === 'function' ? (next as (previous: T) => T)(this.snapshot.value) : next;
    for (const key of Object.keys(value) as (keyof T)[]) {
      if (JSON.stringify(value[key]) !== JSON.stringify(this.snapshot.value[key])) this.pending[key] = value[key];
    }
    if (!Object.keys(this.pending).length) return;
    this.snapshot = { value, saving: true, error: null };
    this.emit();
    void this.flush();
  };
  flush = async () => {
    if (this.running) return;
    this.running = true;
    this.snapshot = { ...this.snapshot, saving: true, error: null };
    this.emit();
    try {
      while (Object.keys(this.pending).length) {
        const patch = this.pending;
        this.pending = {};
        this.active = patch;
        try { await this.save(patch, this.snapshot.value); }
        catch (error) {
          this.pending = { ...patch, ...this.pending };
          this.snapshot = { ...this.snapshot, error: error instanceof Error ? error.message : 'Could not save changes.' };
          return;
        }
      }
    } finally {
      this.active = {};
      this.running = false;
      this.snapshot = { ...this.snapshot, saving: false };
      this.emit();
    }
  };
}
